import React, { CSSProperties, useState } from "react";
import { RiPencilFill } from "react-icons/ri";
import { FaPlus, FaEye, FaEdit } from "react-icons/fa";
import BasicDataTable from "../../../../Component/BasicComponents/BasicDataTable";
import CommonModalForNewPolicy from "../CommonModalNewPolicy/CommonModalForNewPolicy";
import PageHeader from "../../../../Component/BasicComponents/HeaderBreadCrumbs";
import {
  useAddPolicyMaster,
  useGetOrgUnitsData,
  usePolicyMasterData,
  useUpdatePolicyMaster,
} from "../hooks/useGradeMaster";

interface TableButtonProps {
  onClick: () => void;
  title?: string;
  style?: CSSProperties;
}

const PolicyMaster = () => {
  // States
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalContentState, setModalContentState] = useState("");
  const [selectedRowData, setSelectedRowData] = useState(null);

  // API's Data Hooks
  const { data: policyMasterData } = usePolicyMasterData({ enable: true });
  const { data: orgUnitsData } = useGetOrgUnitsData({ enable: true });
  const { mutate } = useAddPolicyMaster();
  const { mutate: updatePolicyMasters } = useUpdatePolicyMaster();

  const formattedOrgUnitData = Object.entries(orgUnitsData?.data || {}).map(
    (item: any, index: any) => ({
      id: item[0],
      label: item[1],
    })
  );

  // Formatted Data For the Table
  const formattedData =
    policyMasterData?.data?.map((item: any, index: number) => ({
      ...item,
      Sr: index + 1, // Serial number starts from 1
    })) || [];

  // Modal Toggle
  const toggleModal = () => {
    setIsModalOpen((prev) => !prev);
    setModalContentState("");
  };

  // Handle Modal Cancel Button
  const handleCancilBtn = () => {
    setIsModalOpen(false);
  };

  // Handle Submit Button
  const handleSubmit = (formData: any) => {
    const addBody = {
      policyName: formData?.name,
      policyCode: formData?.shortcode,
      currencyId: formData?.currency,
      orgUnitId: formData?.orgunit,
      startDate: formData?.startdate,
      endDate: formData?.enddate,
    };

    const updateBody = {
      policyId: formData?.policyid,
      policyName: formData?.name,
      policyCode: formData?.shortcode,
      currencyId: formData?.currency,
      orgUnitId: formData?.orgunit,
      startDate: formData?.startdate,
      endDate: formData?.enddate,
    };

    toggleModal(); // Close the modal after submission
    setModalContentState("");

    if (modalContentState === "Add") {
      mutate(addBody, {
        onSuccess: () => {
          handleCancilBtn();
          setModalContentState("");
          setSelectedRowData(null);
        },
      });
    } else {
      updatePolicyMasters(updateBody, {
        onSuccess: () => {
          handleCancilBtn();
          setModalContentState("");
          setSelectedRowData(null);
        },
      });
    }
  };

  // Handle Policy View Button
  const handlePolicyMasterRequestView = (row: any) => {
    setIsModalOpen(!isModalOpen);
    setModalContentState("View");
    setSelectedRowData(row);
  };

  // Handle Policy Edit Button
  const handlePolicyMasterRequestEdit = (row: any) => {
    setIsModalOpen(!isModalOpen);
    setModalContentState("Edit");
    setSelectedRowData(row);
  };

  // Handle Policy Table Header Button
  const handleTableHeaderButton = () => {
    setIsModalOpen(!isModalOpen);
    setModalContentState("Add");
    setSelectedRowData(null);
  };

  return (
    <div className="p-4">
      <PageHeader title="Policy Master" />

      <div className="row mb-0 ">
        <div className="col-md-12">
          <BasicDataTable
            Title="Policies"
            data={formattedData || []}
            columns={PolicyMasterDataColumns(
              handlePolicyMasterRequestEdit,
              handlePolicyMasterRequestView
            )}
            headerContent={
              <TableButton
                onClick={handleTableHeaderButton}
                title="New Policy"
              />
            }
          />
        </div>
      </div>
      <CommonModalForNewPolicy
        title={
          modalContentState === "Add"
            ? "Add New Policy"
            : modalContentState === "Edit"
            ? "Edit Policy"
            : "View Policy"
        }
        isModalOpen={isModalOpen}
        toggleModal={toggleModal}
        handleSubmit={handleSubmit}
        onCancilBtn={handleCancilBtn}
        modalContentState={modalContentState}
        selectedRowData={selectedRowData}
        // formattedCurrenciesData={formattedCurrenciesData}
      />
    </div>
  );
};
export default PolicyMaster;

const PolicyMasterDataColumns = (
  handlePolicyMasterRequestEdit: (row: any) => void,
  handlePolicyMasterRequestView: (row: any) => void
) => [
  {
    name: "Sr.",
    selector: (row: { Sr: number | string }) => row.Sr,
  },
  {
    name: "Name",
    selector: (row: { PolicyName: string }) => row.PolicyName,
  },
  {
    name: "Policy Code ",
    selector: (row: { PolicyCode: string }) => row.PolicyCode,
  },
  {
    name: "Org Unit",
    selector: (row: { UnitName: string }) => row.UnitName,
  },
  {
    name: "Start Date",
    selector: (row: { StartDate: string }) => row.StartDate,
  },
  {
    name: "End Date",
    selector: (row: { EndDate: string }) => row.EndDate,
  },
  {
    name: "Actions",
    width: "200px",
    cell: (row: any) => (
      <div
        className="d-flex align-items-center gap-2" /* style={{ display: "flex", gap: "4px" }} */
      >
        <button
          onClick={() => handlePolicyMasterRequestEdit(row)}
          className="btn btn-primary"
          style={{ borderRadius: "20px", padding: "5px", width: "80px" }}
        >
          <FaEdit style={{ fill: "#fff" }} /> Edit
        </button>

        <button
          onClick={() => handlePolicyMasterRequestView(row)}
          className="btn btn-primary"
          style={{ borderRadius: "20px", padding: "5px", width: "80px" }}
        >
          <FaEye style={{ fill: "#fff" }} /> View
        </button>
      </div>
    ),
  },
];

const TableButton: React.FC<TableButtonProps> = ({
  onClick,
  title = "Add Heads",
  style,
}) => {
  const defaultStyle: CSSProperties = {
    display: "flex",
    borderRadius: "20px",
    padding: "2px",
    width: "110px",
    height: "40px",
    justifyContent: "center",
    textAlign: "center",
    alignItems: "center",
  };

  return (
    <div>
      <button
        onClick={onClick}
        className="btn btn-info"
        style={{ ...defaultStyle, ...style }} // External styles override default
      >
        <FaPlus /> <span style={{ marginLeft: "5px" }}>{title}</span>
      </button>
    </div>
  );
};
